import { Common } from "@common/types";
import * as React from "react";

export type KeyboardNavigationChildrenArgs = {
  ref: React.RefObject<any>;
};

export type KeyboardNavigationMode = "default" | "arrows" | "search";
export type KeyboardNavigationProps = {
  items: Common.LabelValue<Common.Primitive>[];
  mode: KeyboardNavigationMode;
  onItemSelected: (key: Common.Primitive) => void;

  children: (args: KeyboardNavigationChildrenArgs) => any;
};

const KeyboardNavigation = (props: KeyboardNavigationProps) => {
  const ref = React.createRef<HTMLElement>();

  React.useEffect(() => {
    if (!ref.current) return;

    const containerRef = ref.current;

    const keyDown = (e: KeyboardEvent) => {
      if (containerRef) {
        if (e.key === "ArrowDown" || (e.key === "ArrowUp" && (props.mode === "default" || props.mode === "arrows"))) {
          e.preventDefault();

          const focusedEl = containerRef.querySelector(":focus");
          const toFocus = (
            focusedEl
              ? e.key === "ArrowDown"
                ? focusedEl.nextSibling
                : focusedEl.previousSibling
              : e.key === "ArrowDown"
                ? containerRef.firstChild
                : containerRef.lastChild
          ) as HTMLElement;
          toFocus && toFocus.focus && toFocus.focus();
        } else if (e.key === "Enter") {
          const focusedEl = containerRef.querySelector(":focus") as HTMLElement;
          if (focusedEl) {
            const key = focusedEl.dataset["key"];
            if (key) {
              const opt = props.items.find((x) => (x.value ?? "").toString() === key);
              opt && props.onItemSelected(opt.value);
            }
          }
        } else if (props.mode === "default" || props.mode === "search") {
          const keycode = e.keyCode;

          const valid =
            (keycode > 47 && keycode < 58) || // number keys
            (keycode > 64 && keycode < 91) || // letter keys
            (keycode > 95 && keycode < 112) || // numpad keys
            (keycode > 218 && keycode < 223); // [\]' (in order)

          if (valid) {
            const key = e.key.toLowerCase();
            const opt = props.items.find((x) => x.label.toLowerCase().startsWith(key));
            if (opt) {
              const toFocus = containerRef.querySelector(`[data-key='${opt.value}']`) as HTMLElement;
              toFocus && toFocus.focus && toFocus.focus();
            }
          }
        }
      }
    };

    if (containerRef) {
      window.addEventListener("keydown", keyDown);
    }

    return () => {
      if (containerRef) {
        window.removeEventListener("keydown", keyDown);
      }
    };
  }, [ref, props.onItemSelected, props.items, props.mode]);

  return props.children({
    ref
  });
};

export default KeyboardNavigation;
